import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Search as SearchIcon, MapPin, Briefcase, Lock } from "lucide-react";

const VISA_TYPES = ["Skilled Worker", "Global Talent", "Health & Care Worker", "Student", "Graduate", "Dependent", "ILR", "HSMP", "British Citizen (OCI)", "482 TSS (Skills in Demand)", "189 Skilled Independent", "190 Skilled Nominated", "491 Regional", "500 Student", "485 Graduate", "Partner Visa", "Permanent Resident", "Australian Citizen (OCI)", "Other"];

const CASTES = ["Brahmin", "Iyer", "Iyengar", "Nair", "Reddy", "Kamma", "Naidu", "Chettiar", "Mudaliar", "Pillai", "Vanniyar", "Gounder", "Ezhava", "Nadar", "Maratha", "Rajput", "Kshatriya", "Jat", "Patel", "Bania", "Agarwal", "Gupta", "Kayastha", "Khatri", "Sindhi", "Yadav", "Kurmi", "Vokkaliga", "Lingayat", "OBC", "SC/ST", "Christian", "Muslim", "Sikh", "Jain", "Parsi", "Other"];

export default function SearchPage() {
  const [filters, setFilters] = useState({ visa_type: "any", residence_country: "any", community: "any", min_age: "", max_age: "" });
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [total, setTotal] = useState(0);

  const run = async () => {
    setBusy(true);
    try {
      const payload = {
        visa_type: filters.visa_type && filters.visa_type !== "any" ? filters.visa_type : null,
        residence_country: filters.residence_country && filters.residence_country !== "any" ? filters.residence_country : null,
        community: filters.community && filters.community !== "any" ? filters.community : null,
        min_age: filters.min_age ? Number(filters.min_age) : null,
        max_age: filters.max_age ? Number(filters.max_age) : null,
        page: 1, page_size: 24,
      };
      const r = await api.post("/profiles/search", payload);
      setResults(r.data.profiles);
      setTotal(r.data.total);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { run(); /* initial */ /* eslint-disable-next-line */ }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-slate-900">Find your match</h1>
      <p className="text-slate-600 mt-1">Filter by visa, community and age.</p>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mt-8">
        <aside className="lg:col-span-1">
          <Card className="p-5 border-[#EAE3D2] bg-white sticky top-24">
            <h3 className="font-serif text-xl text-slate-900 mb-4">Filters</h3>
            <div className="space-y-3">
              <div>
                <Label>Country</Label>
                <Select value={filters.residence_country} onValueChange={(v) => setFilters({ ...filters, residence_country: v })}>
                  <SelectTrigger data-testid="filter-country"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="Australia">Australia</SelectItem>
                    <SelectItem value="India">India</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Visa type</Label>
                <Select value={filters.visa_type} onValueChange={(v) => setFilters({ ...filters, visa_type: v })}>
                  <SelectTrigger data-testid="filter-visa"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {VISA_TYPES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Community / Caste</Label>
                <Select value={filters.community} onValueChange={(v) => setFilters({ ...filters, community: v })}>
                  <SelectTrigger data-testid="filter-community"><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    {CASTES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Min age</Label><Input data-testid="filter-min-age" type="number" value={filters.min_age} onChange={(e) => setFilters({ ...filters, min_age: e.target.value })} /></div>
                <div><Label>Max age</Label><Input data-testid="filter-max-age" type="number" value={filters.max_age} onChange={(e) => setFilters({ ...filters, max_age: e.target.value })} /></div>
              </div>
              <Button data-testid="filter-search-btn" onClick={run} disabled={busy} className="w-full bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full mt-2">
                <SearchIcon className="w-4 h-4 mr-2" /> {busy ? "Searching…" : "Search"}
              </Button>
            </div>
          </Card>
        </aside>

        <main className="lg:col-span-3">
          <p className="text-sm text-slate-600 mb-4"><b>{total}</b> profiles found</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {results.map((p) => (
              <ProfileCard key={p.id} p={p} />
            ))}
          </div>
          {results.length === 0 && !busy && (
            <Card className="p-10 text-center border-[#EAE3D2] bg-white">
              <p className="text-slate-600">No matches yet. Try widening your filters.</p>
            </Card>
          )}
        </main>
      </div>
    </div>
  );
}

function isIndianProfile(p) {
  return p?.member_type === "india" || p?.residence_country === "India" || (p?.visa_type || "").toLowerCase() === "not applicable";
}
function displayVisaType(p) {
  if (isIndianProfile(p)) return "Indian";
  return p.visa_type || "—";
}

function ProfileCard({ p }) {
  return (
    <Card className="overflow-hidden border-[#EAE3D2] bg-white hover:shadow-lg transition-shadow" data-testid={`profile-card-${p.id}`}>
      <div className="h-52 bg-[#F3EFE6] relative overflow-hidden">
        {p.profile_photo_path ? (
          <img src={fileUrl(p.profile_photo_path)} alt={p.full_name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#6B1D2F]/10 to-[#D4AF37]/10">
            <span className="font-serif text-5xl text-[#6B1D2F]/50">{p.full_name?.[0]}</span>
          </div>
        )}
        <Badge className="absolute top-3 right-3 bg-[#1E3A8A] text-white" data-testid={`visa-type-${p.id}`}>{displayVisaType(p)}</Badge>
      </div>
      <div className="p-5">
        <h3 className="font-serif text-xl text-slate-900">{p.full_name}, <span className="text-slate-600">{p.age}</span></h3>
        <p className="text-xs uppercase tracking-wider text-[#B8860B] font-semibold mt-1">{p.caste} · {p.mother_tongue}</p>
        <div className="mt-3 space-y-1.5 text-sm text-slate-700">
          <div className="flex items-center gap-2"><Briefcase className="w-4 h-4 text-slate-500" /> {p.profession} at {p.company}</div>
          <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-500" /> {p.current_city || <span className="masked-field">London XYZ</span>}</div>
          {isIndianProfile(p) ? (
            <div className="flex items-center gap-2 text-xs text-slate-500"><MapPin className="w-3 h-3" /> India-based member</div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-slate-500"><Lock className="w-3 h-3" /> Visa valid till {p.visa_expiry_date}</div>
          )}
        </div>
        <Link to={`/profile/${p.id}`} data-testid={`view-profile-${p.id}`}>
          <Button variant="outline" className="w-full mt-4 border-[#6B1D2F]/30 text-[#6B1D2F] hover:bg-[#6B1D2F]/5 rounded-full">View profile</Button>
        </Link>
      </div>
    </Card>
  );
}
